// src/components/PortfolioSidebar/portfolio_sidebar.js

import SidebarShell from "@/components/SidebarShell/SidebarShell.vue";
import SidebarContext from "@/components/SidebarContext/SidebarContext.vue";
import SidebarUserFooter from "@/components/SidebarUserFooter/SidebarUserFooter.vue";
import brand_details from "@/data/brand_details";
import { SIDEBAR_CONFIG, ROLE_TO_APP_KEY } from "@/data/sidebar_config";

// IMPORT UTILITIES
import { get_user_profile } from "@/router";

const details = brand_details();

export default {
  name: "PortfolioSidebar",

  components: ({
    SidebarShell,
    SidebarContext,
    SidebarUserFooter
  }),

  props: {
    /**
     * Optional override from parent.
     * fund_manager | donor_portal | governance | entrepreneur
     */
    app_key: ({
      type: String,
      required: false,
      default: null
    }),
    /**
     * Optional active item override from parent.
     */
    active_item: ({
      type: String,
      required: false,
      default: null
    })
  },

  data() {
    return ({
      // Brand Information
      brand_name: details.name,
      brand_subtitle: details.subtitle,
      entity_name: details.sub_entity_name,

      // Context State
      selected_portfolio: details.sub_entity_name,

      // ✅ default active
      active_menu_item: "social_impact_intelligence",


      // Footer Data
      full_name: "Guest User",
      initials: "GU",

      // Brand Logo
      short_logo: details.logo,

      // ✅ resolved sidebar config key
      resolved_app_key: "fund_manager"
    });
  },

  computed: {
    sidebar_config() {
      return SIDEBAR_CONFIG[this.resolved_app_key] || SIDEBAR_CONFIG.fund_manager;
    },

    /**
     * ✅ menuItems now comes from sidebar_config.js
     */
    menuItems() {
      return this.sidebar_config.menu || [];
    },

    // ✅ highlight section header when one of its children is active
    is_section_active() {
      return (item) => {
        if (this.active_menu_item === item.key) return true;
        const child_keys = (item.children || []).map(c => c.key);
        return child_keys.includes(this.active_menu_item);
      };
    }
  },

  mounted() {
    this.load_user_name();
    this.resolve_sidebar_from_role();


    // ✅ if current active menu not in this sidebar, default to first menu item
    this.ensure_active_menu_valid();
  },

  watch: {
    active_item(newVal) {
      if (newVal) {
        this.active_menu_item = newVal;
      }
    }
  },

  methods: {

    resolve_sidebar_from_role() {
      const explicit_key = this._get_explicit_app_key();
      if (explicit_key) {
        this.resolved_app_key = explicit_key;
        return;
      }

      const raw_role = this._get_user_role_raw();
      const normalized_role = this._normalize_role(raw_role);
      this.resolved_app_key = this._resolve_app_key_from_role(normalized_role);
    },

    _get_explicit_app_key() {
      const key = this.app_key;
      if (!key) return null;
      return SIDEBAR_CONFIG[String(key)] ? key : null;
    },

    _get_user_role_raw() {
      const user = get_user_profile() || {};

      const role_candidates = [
        user.role_key,
        user.role,
        user.user_role?.role?.name,
        user.user_role?.role_name,
        user.profile_role,
        sessionStorage.getItem("role"),
      ];

      for (const value of role_candidates) {
        if (value !== null && value !== undefined && String(value).trim() !== "") {
          return String(value).trim().toLowerCase();
        }
      }

      return "";
    },

    _normalize_role(raw_role) {
      return String(raw_role || "").replace(/\s+/g, "_");
    },

    _resolve_app_key_from_role(normalized_role) {
      return ROLE_TO_APP_KEY[String(normalized_role)] || "fund_manager";
    },

    /** ✅ make sure active menu exists in current menu list */
    ensure_active_menu_valid() {
      const flat_keys = [];

      (this.menuItems || []).forEach(item => {
        if (item.type === "section" || item.type === "accordion") {
          (item.children || []).forEach(child => flat_keys.push(child.key));
          flat_keys.push(item.key);
        } else if (item.type !== "component") {
          flat_keys.push(item.key);
        }
      });

      // ✅ auto-expand logic if needed (generic check)
      if (this.is_child_of_any_section(this.active_menu_item)) {
        // No more accordion toggle state to update, but logic remains if needed
      }

      if (!flat_keys.includes(this.active_menu_item)) {
        // pick first real item
        const first = (this.menuItems || []).find(i => i.type !== "component");
        if (first?.type === "section" || first?.type === "accordion") {
          this.active_menu_item = first.children?.[0]?.key || first.key;
        } else {
          this.active_menu_item = first?.key || null;
        }
      }
    },

    /** FOOTER NAME / INITIALS */
    load_user_name() {
      const user = get_user_profile();
      let name = "Guest User";

      if (user && (user.first_name || user.last_name)) {
        name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
      } else {
        const first = sessionStorage.getItem("first_name") || "";
        const last = sessionStorage.getItem("last_name") || "";
        name = `${first} ${last}`.trim() || "Guest User";
      }

      if (name === "Guest User" && user?.email) {
        name = user.email.split("@")[0];
      }

      const parts = name.split(" ");
      const initials = ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();

      this.full_name = name;
      this.initials = initials || "GU";
    },

    /** ✅ Section helpers */
    is_child_of_any_section(key) {
      return (this.menuItems || []).some(item =>
        (item.type === "section" || item.type === "accordion") &&
        (item.children || []).some(c => c.key === key)
      );
    },

    /** ✅ MENU SELECTION */
    select_menu_item(menuItem, label = null) {
      this.active_menu_item = menuItem;
      this.$emit("global-menu-selected", { key: menuItem, label: label });
    },

    /** OPTIONAL: keep these if you use them in your parent */
    select_global_portfolio() {
      this.active_menu_item = "social_impact_intelligence";
      this.$emit("global-selected");
      this.$emit("global-menu-selected", { key: "social_impact_intelligence", label: "Dashboard" });
    }
  }
};